import axios from 'axios';

// Create axios instance with base URL
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api'
});

// Debug logging (removed in production build)
console.log('API Base URL:', process.env.REACT_APP_API_URL || '/api');
console.log('Environment:', process.env.NODE_ENV);
console.log('All env vars:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP')));
console.log('Full REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

// Add token to requests if it exists
API.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    console.log('API Request:', config.url, 'Token:', token ? 'Present' : 'Missing');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Handle token expiration and other common errors
API.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error);
    console.error('Error config:', error.config);
    console.error('Error response:', error.response);
    
    if (error.response && error.response.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Set tenant header for multi-tenant requests
export const setTenantHeader = (domain) => {
  if (domain) {
    API.defaults.headers.common['X-Tenant-Domain'] = domain;
  } else {
    delete API.defaults.headers.common['X-Tenant-Domain'];
  }
};

// Auth service
export const authService = {
  login: async (email, password) => {
    const response = await API.post('/users/login', { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      API.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    }
    return response.data;
  },
  
  register: async (userData) => {
    const response = await API.post('/users/register', userData);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      API.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    }
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('token');
    delete API.defaults.headers.common['Authorization'];
  },
  
  getCurrentUser: async () => {
    const response = await API.get('/users/profile');
    return response.data;
  },
  
  updateProfile: async (userData) => {
    const response = await API.put('/users/profile', userData);
    return response.data;
  }
};

// Resource service for fetching voices, models, etc.
export const resourceService = {
  getAvailableResources: async (forceRefresh = false) => {
    const url = forceRefresh 
      ? '/resources/available-resources?forceRefresh=true' 
      : '/resources/available-resources';
    const response = await API.get(url);
    return response.data;
  },
  
  refreshResources: async () => {
    const response = await API.post('/resources/refresh-resources');
    return response.data;
  },
  
  checkCompatibility: async (voiceId, s2sModel) => {
    const response = await API.post('/resources/check-compatibility', {
      voiceId,
      s2sModel
    });
    return response.data;
  }
};

// Agent service
export const agentService = {
  getAgents: async () => {
    const response = await API.get('/agents');
    return response.data;
  },
  
  getAgentById: async (id) => {
    const response = await API.get(`/agents/${id}`);
    return response.data;
  },
  
  createAgent: async (agentData) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await API.post('/agents', agentData, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  },
  
  updateAgent: async (id, agentData) => {
    const response = await API.put(`/agents/${id}`, agentData);
    return response.data;
  },
  
  deleteAgent: async (id) => {
    const response = await API.delete(`/agents/${id}`);
    return response.data;
  }
};

// Call service
export const callService = {
  getCalls: async () => {
    const response = await API.get('/calls');
    return response.data;
  },
  
  getCallById: async (id) => {
    const response = await API.get(`/calls/${id}`);
    return response.data;
  },
  
  createCall: async (callData) => {
    const response = await API.post('/calls', callData);
    return response.data;
  }
};

// Phone Number service
export const phoneNumberService = {
  getPhoneNumbers: async () => {
    const response = await API.get('/phone-numbers');
    return response.data;
  },
  
  getActivePhoneNumbers: async () => {
    const response = await API.get('/phone-numbers/active');
    return response.data;
  },
  
  getPhoneNumberById: async (id) => {
    const response = await API.get(`/phone-numbers/${id}`);
    return response.data;
  },
  
  createPhoneNumber: async (phoneNumberData) => {
    const response = await API.post('/phone-numbers', phoneNumberData);
    return response.data;
  },
  
  updatePhoneNumber: async (id, phoneNumberData) => {
    const response = await API.put(`/phone-numbers/${id}`, phoneNumberData);
    return response.data;
  },
  
  deletePhoneNumber: async (id) => {
    const response = await API.delete(`/phone-numbers/${id}`);
    return response.data;
  },
  
  validatePhoneNumber: async (phoneNumber) => {
    const response = await API.post('/phone-numbers/validate', { phoneNumber });
    return response.data;
  },

  // Twilio integration methods
  testTwilioCredentials: async (accountSid, authToken) => {
    const response = await API.post('/phone-numbers/twilio/test-credentials', { accountSid, authToken });
    return response.data;
  },

  getTwilioPhoneNumbers: async (accountSid, authToken) => {
    const response = await API.post('/phone-numbers/twilio/get-numbers', { accountSid, authToken });
    return response.data;
  },

  importFromTwilio: async (importData) => {
    const response = await API.post('/phone-numbers/twilio/import', importData);
    return response.data;
  }
};

// Dashboard service
export const dashboardService = {
  getDashboardStats: async () => {
    const response = await API.get('/dashboard/stats');
    return response.data;
  }
};

// Analytics service
export const analyticsService = {
  getSummary: async (params = {}) => {
    const response = await API.get('/analytics/summary', { params });
    return response.data.data;
  },
  
  getTrends: async (params = {}) => {
    const response = await API.get('/analytics/trends', { params });
    return response.data.data;
  },
  
  getReasons: async (params = {}) => {
    const response = await API.get('/analytics/reasons', { params });
    return response.data.data;
  },
  
  getAgentPerformance: async (params = {}) => {
    const response = await API.get('/analytics/agent-performance', { params });
    return response.data.data;
  },
  
  getRecentCalls: async (params = {}) => {
    const response = await API.get('/analytics/recent-calls', { params });
    return response.data.data;
  },
  
  getStatistics: async (params = {}) => {
    const response = await API.get('/analytics/statistics', { params });
    return response.data;
  }
};

// Organization service
export const organizationService = {
  createOrganization: async (orgData) => {
    const response = await API.post('/organizations', orgData);
    return response.data;
  },
  
  getOrganizationById: async (id) => {
    const response = await API.get(`/organizations/${id}`);
    return response.data;
  },
  
  updateOrganization: async (id, orgData) => {
    const response = await API.put(`/organizations/${id}`, orgData);
    return response.data;
  },
  
  getOrganizationUsers: async (id) => {
    const response = await API.get(`/organizations/${id}/users`);
    return response.data;
  },
  
  addUserToOrganization: async (id, userData) => {
    const response = await API.post(`/organizations/${id}/users`, userData);
    return response.data;
  },
  
  checkSlugAvailability: async (slug) => {
    const response = await API.get(`/organizations/check-slug/${slug}`);
    return response.data;
  },
  
  getOrganizationByDomain: async (domain) => {
    const response = await API.get(`/organizations/domain/${domain}`);
    return response.data;
  }
};

// White label service
export const whiteLabelService = {
  getWhiteLabelSettings: async (organizationId) => {
    const response = await API.get(`/white-label/${organizationId}`);
    return response.data;
  },
  
  updateWhiteLabelSettings: async (organizationId, settings) => {
    const response = await API.put(`/white-label/${organizationId}`, settings);
    return response.data;
  },
  
  uploadLogo: async (organizationId, formData) => {
    const response = await API.post(`/white-label/${organizationId}/upload-logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  
  uploadFavicon: async (organizationId, formData) => {
    const response = await API.post(`/white-label/${organizationId}/upload-favicon`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  
  uploadBackground: async (organizationId, formData) => {
    const response = await API.post(`/white-label/${organizationId}/upload-background`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  
  getWhiteLabelSettingsByDomain: async (domain) => {
    const response = await API.get(`/white-label/domain/${domain}`);
    return response.data;
  }
};

// Widget service
export const widgetService = {
  createWidget: async (widgetData) => {
    // The token is already handled by the API interceptor, no need to add it manually
    const response = await API.post('/widgets', widgetData);
    return response.data;
  },
  getWidgets: async (org_id) => {
    const response = await API.get(`/widgets/organization/${org_id}`);
    return response.data;
  },
  updateWidget: async (id, widgetData) => {
    const response = await API.put(`/widgets/${id}`, widgetData);
    return response.data;
  },
  
  deleteWidget: async (id) => {
    const response = await API.delete(`/widgets/${id}`);
    return response.data;
  }
};

// Integration service
export const integrationService = {
  getIntegrations: async () => {
    const response = await API.get('/integrations');
    return response.data;
  },
  
  revokeIntegration: async (integrationId) => {
    const response = await API.delete(`/integrations/${integrationId}`);
    return response.data;
  },
  
  // Outlook specific methods
  getOutlookAuthUrl: async () => {
    const response = await API.get('/integrations/outlook/auth');
    return response.data;
  },
  
  // Google specific methods
  getGoogleAuthUrl: async () => {
    const response = await API.get('/integrations/google/auth');
    return response.data;
  },
  
  // GHL specific methods
  getGhlAuthUrl: async () => {
    const response = await API.get('/oauth/ghl/auth');
    return response.data;
  },
  
  getGhlConnectionStatus: async () => {
    const response = await API.get('/oauth/ghl/status');
    return response.data;
  }
};

// Admin service
export const adminService = {
  // Dashboard
  getDashboard: async () => {
    const response = await API.get('/admin/dashboard');
    return response.data;
  },

  // User management
  getUsers: async () => {
    const response = await API.get('/admin/users');
    return response.data;
  },

  updateUser: async (userId, userData) => {
    const response = await API.put(`/admin/users/${userId}`, userData);
    return response.data;
  },

  // Invitation management
  sendInvite: async (inviteData) => {
    const response = await API.post('/admin/invite', inviteData);
    return response.data;
  },

  getInvitations: async () => {
    const response = await API.get('/admin/invitations');
    return response.data;
  },

  cancelInvitation: async (invitationId) => {
    const response = await API.delete(`/admin/invitations/${invitationId}`);
    return response.data;
  },

  acceptInvitation: async (inviteData) => {
    const response = await API.post('/admin/invite/accept', inviteData);
    return response.data;
  },

  // Impersonation
  impersonateUser: async (userId) => {
    const response = await API.post(`/admin/impersonate/${userId}`);
    return response.data;
  },

  stopImpersonation: async () => {
    const response = await API.post('/admin/stop-impersonation');
    return response.data;
  }
};

// API Key service
export const apiKeyService = {
  getApiKeys: async () => {
    const response = await API.get('/keys');
    return response.data;
  },
  
  createApiKey: async (apiKeyData) => {
    const response = await API.post('/keys', apiKeyData);
    return response.data;
  },
  
  updateApiKey: async (id, apiKeyData) => {
    const response = await API.put(`/keys/${id}`, apiKeyData);
    return response.data;
  },
  
  deleteApiKey: async (id) => {
    const response = await API.delete(`/keys/${id}`);
    return response.data;
  },
  
  regenerateApiKey: async (id) => {
    const response = await API.post(`/keys/${id}/regenerate`);
    return response.data;
  }
};

// Contact service
export const contactService = {
  getContacts: async (params = {}) => {
    const response = await API.get('/contacts', { params });
    return response.data;
  },
  
  getContactById: async (id) => {
    const response = await API.get(`/contacts/${id}`);
    return response.data;
  },
  
  createContact: async (contactData) => {
    const response = await API.post('/contacts', contactData);
    return response.data;
  },
  
  updateContact: async (id, contactData) => {
    const response = await API.put(`/contacts/${id}`, contactData);
    return response.data;
  },
  
  deleteContact: async (id) => {
    const response = await API.delete(`/contacts/${id}`);
    return response.data;
  },
  
  getContactCalls: async (id) => {
    const response = await API.get(`/contacts/${id}/calls`);
    return response.data;
  },
  
  searchContacts: async (query, options = {}) => {
    const response = await API.post('/contacts/search', { query, ...options });
    return response.data;
  },
  
  getContactTimeline: async (id) => {
    const response = await API.get(`/contacts/${id}/timeline`);
    return response.data;
  }
};

// Field Extraction Config service
export const fieldExtractionService = {
  getConfigs: async () => {
    const response = await API.get('/field-configs');
    return response.data;
  },
  
  getConfigById: async (id) => {
    const response = await API.get(`/field-configs/${id}`);
    return response.data;
  },
  
  createConfig: async (configData) => {
    const response = await API.post('/field-configs', configData);
    return response.data;
  },
  
  updateConfig: async (id, configData) => {
    const response = await API.put(`/field-configs/${id}`, configData);
    return response.data;
  },
  
  deleteConfig: async (id) => {
    const response = await API.delete(`/field-configs/${id}`);
    return response.data;
  },
  
  getTemplates: async () => {
    const response = await API.get('/field-configs/default-templates');
    return response.data;
  }
};

export default API;
